import { readonly, ref, shallowRef, toValue, watch, type MaybeRefOrGetter } from 'vue';
import { onDispose, unrefElement, type NxMaybeElement } from './element';

export interface NxInfiniteScrollOptions {
  /** Whether another page exists. Loading stops once it turns false. */
  hasMore: MaybeRefOrGetter<boolean>;
  /** Pause loading (a filter is being applied, a dialog is open). */
  disabled?: MaybeRefOrGetter<boolean>;
  /** Scroll container; default the viewport. */
  root?: NxMaybeElement;
  /** How early to start, before the sentinel is actually visible. Default `'200px'`. */
  rootMargin?: string;
  /** First wait after a failed load; doubles per failure up to 30 s. Default 2000 ms. */
  retryMs?: number;
}

const MAX_RETRY_MS = 30_000;

/**
 * Loads the next page when a sentinel element at the end of a list scrolls
 * into view. `loadMore` must resolve once the new rows are in.
 *
 * One visibility → one load. After each load the sentinel is observed afresh,
 * which re-measures it once: a short page that leaves the sentinel on screen
 * loads the next page without waiting for a scroll, and one that pushes it
 * away stops. A failed load keeps its error and tries again after a growing
 * pause (2 s, 4 s … 30 s) while the sentinel stays in view; `retry()` goes
 * at once. Without IntersectionObserver nothing loads on its own — pair it
 * with a button (NxLoadMore) that calls `loadNow()`.
 */
export function useNxInfiniteScroll(
  sentinel: NxMaybeElement,
  loadMore: () => Promise<unknown> | unknown,
  options: NxInfiniteScrollOptions
) {
  const loading = ref(false);
  const error = shallowRef<unknown>(null);
  const supported = typeof IntersectionObserver !== 'undefined';
  let observer: IntersectionObserver | null = null;
  let failures = 0;
  let retryTimer: ReturnType<typeof setTimeout> | null = null;
  let disposed = false;

  const blocked = () =>
    disposed || loading.value || !toValue(options.hasMore) || !!toValue(options.disabled);

  const clearRetry = () => {
    if (retryTimer !== null) clearTimeout(retryTimer);
    retryTimer = null;
  };

  function observe() {
    observer?.disconnect();
    observer = null;
    const el = unrefElement(sentinel);
    if (!supported || !el || disposed || !toValue(options.hasMore) || toValue(options.disabled)) {
      return;
    }
    const root = options.root ? unrefElement(options.root) : null;
    observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) void run(false);
      },
      { root, rootMargin: options.rootMargin ?? '200px' }
    );
    observer.observe(el);
  }

  async function run(manual: boolean): Promise<void> {
    if (blocked()) return;
    // An automatic trigger waits out the back-off; a click or retry() does not.
    if (!manual && retryTimer !== null) return;
    clearRetry();
    loading.value = true;
    // Stop listening while loading; observing afresh afterwards is the re-check.
    observer?.disconnect();
    observer = null;
    try {
      await loadMore();
      if (disposed) return;
      error.value = null;
      failures = 0;
    } catch (err) {
      if (disposed) return;
      error.value = err;
      failures += 1;
      const wait = Math.min(MAX_RETRY_MS, (options.retryMs ?? 2000) * 2 ** (failures - 1));
      retryTimer = setTimeout(() => {
        retryTimer = null;
        observe();
      }, wait);
      return;
    } finally {
      loading.value = false;
    }
    observe();
  }

  const stopWatch = watch(
    () =>
      [
        unrefElement(sentinel),
        options.root ? unrefElement(options.root) : null,
        !!toValue(options.hasMore),
        !!toValue(options.disabled),
      ] as const,
    () => {
      if (!loading.value && retryTimer === null) observe();
    },
    { immediate: true, flush: 'post' }
  );

  const stop = () => {
    disposed = true;
    stopWatch();
    clearRetry();
    observer?.disconnect();
    observer = null;
  };
  onDispose(stop);

  return {
    loading: readonly(loading),
    error: readonly(error),
    /** Load the next page now (a button press); skips while loading or at the end. */
    loadNow: () => run(true),
    /** After a failure: forget the back-off and try again at once. */
    retry: () => {
      failures = 0;
      return run(true);
    },
    stop,
  };
}
